import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import { useApp } from 'contexts/AppContext';
import { useLeague } from 'contexts/LeagueContext';
import NavigationBar from 'components/ui/NavigationBar';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import { formatGameRecap, shareRecap } from 'utils/shareRecap';

const STORAGE_KEY = 'hoopstats_live_game';

const today = () => new Date().toISOString().split('T')[0];

const LiveGamePage = () => {
  const navigate = useNavigate();
  const { players, playersLoading, fetchGamesWithStats } = useApp();
  const { activeLeague, isAdmin } = useLeague();

  const [phase, setPhase] = useState('setup'); // 'setup' | 'play' | 'end'
  const [selectedIds, setSelectedIds] = useState([]);
  const [teams, setTeams] = useState({}); // playerId → 1 | 2
  const [location, setLocation] = useState('');
  const [scores, setScores] = useState({}); // playerId → number
  const [actionLog, setActionLog] = useState([]); // [{ playerId, points, id }]
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [shareStatus, setShareStatus] = useState(null);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);
      if (state.leagueId !== activeLeague?.id) return;
      setPhase(state.phase || 'setup');
      setSelectedIds(state.selectedIds || []);
      setTeams(state.teams || {});
      setLocation(state.location || '');
      setScores(state.scores || {});
      setActionLog(state.actionLog || []);
    } catch {}
  }, [activeLeague?.id]);

  // Persist to localStorage on every change (except after save)
  useEffect(() => {
    if (saved) return;
    if (phase === 'setup' && selectedIds.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        leagueId: activeLeague?.id,
        phase,
        selectedIds,
        teams,
        location,
        scores,
        actionLog,
      }));
    } catch {}
  }, [phase, selectedIds, teams, location, scores, actionLog, activeLeague?.id, saved]);

  const selectedPlayers = useMemo(
    () => players?.filter((p) => selectedIds.includes(p.id)) || [],
    [players, selectedIds]
  );

  const team1Players = useMemo(() => selectedPlayers.filter((p) => teams[p.id] === 1), [selectedPlayers, teams]);
  const team2Players = useMemo(() => selectedPlayers.filter((p) => teams[p.id] === 2), [selectedPlayers, teams]);

  const team1Score = useMemo(() => team1Players.reduce((s, p) => s + (scores[p.id] || 0), 0), [team1Players, scores]);
  const team2Score = useMemo(() => team2Players.reduce((s, p) => s + (scores[p.id] || 0), 0), [team2Players, scores]);

  // --- Setup phase handlers ---
  const togglePlayer = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setTeams((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  const assignTeam = useCallback((id, team) => {
    setTeams((prev) => ({ ...prev, [id]: team }));
  }, []);

  const canStartGame = useMemo(() => {
    if (selectedIds.length < 2) return false;
    const t1 = selectedIds.filter((id) => teams[id] === 1);
    const t2 = selectedIds.filter((id) => teams[id] === 2);
    return t1.length > 0 && t2.length > 0 && t1.length + t2.length === selectedIds.length;
  }, [selectedIds, teams]);

  const startGame = useCallback(() => {
    const initial = {};
    selectedIds.forEach((id) => { initial[id] = 0; });
    setScores(initial);
    setActionLog([]);
    setPhase('play');
  }, [selectedIds]);

  // --- Play phase handlers ---
  const addPoints = useCallback((playerId, pts) => {
    setScores((prev) => ({ ...prev, [playerId]: (prev[playerId] || 0) + pts }));
    setActionLog((prev) => [...prev, { playerId, points: pts, id: Date.now() }]);
  }, []);

  const undo = useCallback(() => {
    setActionLog((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setScores((s) => ({ ...s, [last.playerId]: (s[last.playerId] || 0) - last.points }));
      return prev.slice(0, -1);
    });
  }, []);

  const endGame = useCallback(() => {
    setPhase('end');
  }, []);

  // --- End phase handlers ---
  const winningTeam = team1Score >= team2Score ? 1 : 2;

  const handleSave = useCallback(async () => {
    if (!supabase || !activeLeague) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const { data: gameData, error: gameError } = await supabase
        .from('games')
        .insert({
          date: today(),
          location: location || '',
          winning_team: 'Winners',
          notes: '',
          league_id: activeLeague.id,
        })
        .select()
        .single();
      if (gameError) throw gameError;

      const statsRows = selectedIds.map((id) => ({
        game_id: gameData.id,
        player_id: id,
        team: teams[id] === winningTeam ? 'Winners' : 'Losers',
        points: scores[id] || 0,
        rebounds: 0,
        assists: 0,
        steals: 0,
        blocks: 0,
      }));

      const { error: statsError } = await supabase.from('game_stats').insert(statsRows);
      if (statsError) throw statsError;

      await fetchGamesWithStats();
      localStorage.removeItem(STORAGE_KEY);
      setSaved(true);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save game.');
    } finally {
      setIsSaving(false);
    }
  }, [activeLeague, selectedIds, teams, scores, location, winningTeam, fetchGamesWithStats]);

  const handleShare = useCallback(async () => {
    const playerMap = {};
    players?.forEach((p) => { playerMap[p.id] = p.name; });
    const winnersWithStats = selectedIds
      .filter((id) => teams[id] === winningTeam)
      .map((id) => ({ name: playerMap[id] || 'Unknown', points: scores[id] || 0, rebounds: 0, assists: 0, steals: 0, blocks: 0 }));
    const losersWithStats = selectedIds
      .filter((id) => teams[id] !== winningTeam)
      .map((id) => ({ name: playerMap[id] || 'Unknown', points: scores[id] || 0, rebounds: 0, assists: 0, steals: 0, blocks: 0 }));
    const wScore = winnersWithStats.reduce((s, p) => s + p.points, 0);
    const lScore = losersWithStats.reduce((s, p) => s + p.points, 0);
    const text = formatGameRecap({ date: today(), location, winners: winnersWithStats, losers: losersWithStats, winnersScore: wScore, losersScore: lScore });
    setShareStatus('sharing');
    const result = await shareRecap(text, () => setShareStatus('copied'));
    if (result === 'shared') setShareStatus(null);
    if (result === 'copied') setTimeout(() => setShareStatus(null), 2500);
  }, [players, selectedIds, teams, scores, winningTeam, location]);

  const resetGame = useCallback(() => {
    setPhase('setup');
    setSelectedIds([]);
    setTeams({});
    setLocation('');
    setScores({});
    setActionLog([]);
    setSaved(false);
    setSaveError(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const backToPlay = useCallback(() => {
    setPhase('play');
  }, []);

  // --- Render ---
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <NavigationBar />
      <main className="main-content">
        <div className="px-4 md:px-6 lg:px-8 py-6 max-w-2xl mx-auto">
          {!isAdmin && (
            <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200">
              <Icon name="Info" size={16} color="#2563EB" />
              <span className="font-caption text-sm" style={{ color: '#2563EB' }}>Only league admins can record games.</span>
            </div>
          )}

          {phase === 'setup' && (
            <SetupPhase
              players={players}
              playersLoading={playersLoading}
              selectedIds={selectedIds}
              teams={teams}
              location={location}
              onTogglePlayer={togglePlayer}
              onAssignTeam={assignTeam}
              onLocationChange={setLocation}
              canStart={canStartGame && isAdmin}
              onStart={startGame}
              onCancel={() => navigate('/game-recording')}
            />
          )}

          {phase === 'play' && (
            <PlayPhase
              team1Players={team1Players}
              team2Players={team2Players}
              scores={scores}
              team1Score={team1Score}
              team2Score={team2Score}
              actionLog={actionLog}
              players={players}
              onAddPoints={addPoints}
              onUndo={undo}
              onEndGame={endGame}
              onAbandon={resetGame}
            />
          )}

          {phase === 'end' && (
            <EndPhase
              team1Players={team1Players}
              team2Players={team2Players}
              scores={scores}
              team1Score={team1Score}
              team2Score={team2Score}
              winningTeam={winningTeam}
              location={location}
              isSaving={isSaving}
              saveError={saveError}
              saved={saved}
              shareStatus={shareStatus}
              onSave={handleSave}
              onShare={handleShare}
              onBackToPlay={backToPlay}
              onNewGame={resetGame}
              onGoToHistory={() => navigate('/history')}
              isAdmin={isAdmin}
            />
          )}
        </div>
      </main>
    </div>
  );
};

// ──────────────────────────────────────────────
// Setup Phase
// ──────────────────────────────────────────────
const SetupPhase = ({ players, playersLoading, selectedIds, teams, location, onTogglePlayer, onAssignTeam, onLocationChange, canStart, onStart, onCancel }) => {
  const unassigned = selectedIds.filter((id) => !teams[id]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: 'var(--color-foreground)' }}>Live Game</h1>
        <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Select players and split into teams to start scoring</p>
      </div>

      {/* Location (optional) */}
      <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <label className="font-caption text-xs font-semibold block mb-1.5" style={{ color: 'var(--color-muted-foreground)' }}>Location (optional)</label>
        <input
          type="text"
          value={location}
          onChange={(e) => onLocationChange(e.target.value)}
          placeholder="e.g. Main Court"
          className="w-full px-3 py-2 rounded-lg border font-body text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          style={{ backgroundColor: 'var(--color-muted)', borderColor: 'var(--color-input)', color: 'var(--color-foreground)' }}
          autoCapitalize="words"
        />
      </div>

      {/* Player selection with team assignment */}
      <div className="rounded-xl border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-base" style={{ color: 'var(--color-foreground)' }}>Select Players</h3>
          <span className="font-data text-sm font-semibold px-2.5 py-1 rounded-lg" style={{ backgroundColor: 'rgba(232,93,4,0.12)', color: 'var(--color-primary)' }}>
            {selectedIds.length}/{players?.length || 0}
          </span>
        </div>

        {unassigned.length > 0 && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200">
            <Icon name="AlertCircle" size={14} color="#D97706" />
            <span className="font-caption text-xs text-amber-700">{unassigned.length} player{unassigned.length !== 1 ? 's' : ''} need a team</span>
          </div>
        )}

        {playersLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-4 rounded-full animate-spin" style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-primary)' }} />
          </div>
        ) : players?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
            <p className="font-caption text-sm mt-2" style={{ color: 'var(--color-muted-foreground)' }}>No players yet. <button className="underline" style={{ color: 'var(--color-primary)' }} onClick={() => {}}>Add players first</button></p>
          </div>
        ) : (
          <div className="space-y-2">
            {players.map((player) => {
              const selected = selectedIds.includes(player.id);
              const team = teams[player.id];
              return (
                <div
                  key={player.id}
                  className={`rounded-xl border-2 transition-all ${selected ? 'border-[var(--color-primary)] bg-orange-50 dark:bg-orange-950/20' : 'border-[var(--color-border)]'}`}
                >
                  <button
                    onClick={() => onTogglePlayer(player.id)}
                    className="w-full text-left p-3 flex items-center gap-3 min-h-[52px]"
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-colors ${selected ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-gray-300 bg-white dark:bg-gray-700'}`}>
                      {selected && <Icon name="Check" size={12} color="#fff" strokeWidth={3} />}
                    </div>
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                      <Image src={player.avatar_url || ''} alt={player.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>{player.name}</p>
                      <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{player.position}</p>
                    </div>
                  </button>
                  {selected && (
                    <div className="px-3 pb-3 flex gap-2">
                      <button
                        onClick={() => onAssignTeam(player.id, 1)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border font-caption text-xs font-semibold transition-all ${team === 1 ? 'border-blue-500 bg-blue-500 text-white' : 'border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:border-blue-400'}`}
                      >
                        Team 1
                      </button>
                      <button
                        onClick={() => onAssignTeam(player.id, 2)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border font-caption text-xs font-semibold transition-all ${team === 2 ? 'border-purple-500 bg-purple-500 text-white' : 'border-[var(--color-border)] bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:border-purple-400'}`}
                      >
                        Team 2
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Start / Cancel */}
      <div className="flex gap-3">
        <Button variant="outline" fullWidth onClick={onCancel}>Cancel</Button>
        <Button variant="default" fullWidth onClick={onStart} disabled={!canStart} iconName="Play" iconPosition="left">Start Game</Button>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Play Phase
// ──────────────────────────────────────────────
const PlayPhase = ({ team1Players, team2Players, scores, team1Score, team2Score, actionLog, players, onAddPoints, onUndo, onEndGame, onAbandon }) => {
  const [showAbandon, setShowAbandon] = useState(false);
  const playerMap = useMemo(() => {
    const m = {};
    players?.forEach((p) => { m[p.id] = p; });
    return m;
  }, [players]);
  const lastAction = actionLog.length > 0 ? actionLog[actionLog.length - 1] : null;

  return (
    <div className="space-y-4">
      {/* Scoreboard */}
      <div className="rounded-2xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
        <div className="flex items-stretch">
          <div className={`flex-1 text-center py-5 ${team1Score > team2Score ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}>
            <p className="font-caption text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-muted-foreground)' }}>Team 1</p>
            <p className="font-data text-4xl font-bold" style={{ color: team1Score > team2Score ? '#2563eb' : 'var(--color-foreground)' }}>{team1Score}</p>
          </div>
          <div className="flex items-center px-3">
            <span className="font-data text-lg font-bold" style={{ color: 'var(--color-muted-foreground)' }}>vs</span>
          </div>
          <div className={`flex-1 text-center py-5 ${team2Score > team1Score ? 'bg-purple-50 dark:bg-purple-950/20' : ''}`}>
            <p className="font-caption text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--color-muted-foreground)' }}>Team 2</p>
            <p className="font-data text-4xl font-bold" style={{ color: team2Score > team1Score ? '#7c3aed' : 'var(--color-foreground)' }}>{team2Score}</p>
          </div>
        </div>
      </div>

      {/* Undo bar */}
      <div className="flex items-center gap-2">
        <button
          onClick={onUndo}
          disabled={actionLog.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg font-caption text-xs font-medium transition-colors disabled:opacity-40"
          style={{ backgroundColor: 'var(--color-muted)', color: 'var(--color-foreground)' }}
        >
          <Icon name="Undo2" size={14} color="currentColor" />
          Undo
        </button>
        {lastAction && (
          <span className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            {playerMap[lastAction.playerId]?.name} +{lastAction.points}
          </span>
        )}
        <span className="ml-auto font-data text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
          {actionLog.length} play{actionLog.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Team columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TeamColumn
          label="Team 1"
          color="#2563eb"
          bgClass="bg-blue-50 dark:bg-blue-950/20"
          borderClass="border-blue-200 dark:border-blue-800"
          players={team1Players}
          scores={scores}
          onAddPoints={onAddPoints}
        />
        <TeamColumn
          label="Team 2"
          color="#7c3aed"
          bgClass="bg-purple-50 dark:bg-purple-950/20"
          borderClass="border-purple-200 dark:border-purple-800"
          players={team2Players}
          scores={scores}
          onAddPoints={onAddPoints}
        />
      </div>

      {/* End / Abandon */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" size="default" onClick={() => setShowAbandon(true)} className="flex-shrink-0">
          Abandon
        </Button>
        <Button variant="default" fullWidth onClick={onEndGame} iconName="Flag" iconPosition="left">
          End Game
        </Button>
      </div>

      {/* Abandon confirmation */}
      {showAbandon && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: 'var(--color-card)' }}>
            <h3 className="font-heading font-bold text-lg mb-2" style={{ color: 'var(--color-foreground)' }}>Abandon game?</h3>
            <p className="font-caption text-sm mb-4" style={{ color: 'var(--color-muted-foreground)' }}>This will discard all scoring data. This can't be undone.</p>
            <div className="flex gap-3">
              <Button variant="outline" fullWidth onClick={() => setShowAbandon(false)}>Keep Playing</Button>
              <Button variant="destructive" fullWidth onClick={() => { setShowAbandon(false); onAbandon(); }}>Abandon</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TeamColumn = ({ label, color, bgClass, borderClass, players, scores, onAddPoints }) => (
  <div className={`rounded-xl border ${borderClass} overflow-hidden`}>
    <div className={`px-4 py-2.5 ${bgClass}`} style={{ borderBottom: `2px solid ${color}22` }}>
      <h3 className="font-heading font-semibold text-sm" style={{ color }}>{label}</h3>
    </div>
    <div className="divide-y divide-[var(--color-border)]">
      {players.map((player) => (
        <div key={player.id} className="px-3 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
            <Image src={player.avatar_url || ''} alt={player.name} className="w-full h-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading font-semibold text-sm truncate" style={{ color: 'var(--color-foreground)' }}>{player.name}</p>
            <p className="font-data text-xs" style={{ color: 'var(--color-muted-foreground)' }}>{scores[player.id] || 0} pts</p>
          </div>
          <div className="flex gap-1.5">
            {[1, 2, 3].map((pts) => (
              <button
                key={pts}
                onClick={() => onAddPoints(player.id, pts)}
                className="w-11 h-11 rounded-lg font-data text-sm font-bold flex items-center justify-center transition-all active:scale-95"
                style={{ backgroundColor: `${color}14`, color, border: `1.5px solid ${color}33` }}
              >
                +{pts}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ──────────────────────────────────────────────
// End Phase
// ──────────────────────────────────────────────
const EndPhase = ({ team1Players, team2Players, scores, team1Score, team2Score, winningTeam, location, isSaving, saveError, saved, shareStatus, onSave, onShare, onBackToPlay, onNewGame, onGoToHistory, isAdmin }) => {
  const winners = winningTeam === 1 ? team1Players : team2Players;
  const losers = winningTeam === 1 ? team2Players : team1Players;
  const wScore = winningTeam === 1 ? team1Score : team2Score;
  const lScore = winningTeam === 1 ? team2Score : team1Score;
  const isTied = team1Score === team2Score;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: 'var(--color-foreground)' }}>
          {saved ? 'Game Saved!' : 'Game Over'}
        </h1>
        <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-muted-foreground)' }}>
          {saved ? 'Your game has been recorded.' : (isTied ? 'It\'s a tie! Confirm which team won, or go back and keep playing.' : 'Review the final score and save.')}
        </p>
      </div>

      {/* Final score */}
      <div className="rounded-2xl border overflow-hidden" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
        <div className="flex items-stretch">
          <div className={`flex-1 text-center py-5 ${winningTeam === 1 ? 'bg-emerald-50 dark:bg-emerald-950/20' : ''}`}>
            <p className="font-caption text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: winningTeam === 1 ? '#059669' : 'var(--color-muted-foreground)' }}>
              {winningTeam === 1 ? 'Winners' : 'Losers'}
            </p>
            <p className="font-data text-4xl font-bold" style={{ color: winningTeam === 1 ? '#059669' : '#dc2626' }}>{team1Score}</p>
            <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Team 1</p>
          </div>
          <div className="flex items-center px-3">
            <span className="font-data text-lg font-bold" style={{ color: 'var(--color-muted-foreground)' }}>—</span>
          </div>
          <div className={`flex-1 text-center py-5 ${winningTeam === 2 ? 'bg-emerald-50 dark:bg-emerald-950/20' : ''}`}>
            <p className="font-caption text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: winningTeam === 2 ? '#059669' : 'var(--color-muted-foreground)' }}>
              {winningTeam === 2 ? 'Winners' : 'Losers'}
            </p>
            <p className="font-data text-4xl font-bold" style={{ color: winningTeam === 2 ? '#059669' : '#dc2626' }}>{team2Score}</p>
            <p className="font-caption text-xs mt-1" style={{ color: 'var(--color-muted-foreground)' }}>Team 2</p>
          </div>
        </div>
      </div>

      {/* Player stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PlayerList label="Winners" color="#059669" players={winners} scores={scores} icon="Trophy" />
        <PlayerList label="Losers" color="#dc2626" players={losers} scores={scores} icon="ThumbsDown" />
      </div>

      {/* Tie warning */}
      {isTied && !saved && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200">
          <Icon name="AlertTriangle" size={16} color="#D97706" />
          <span className="font-caption text-sm text-amber-700">Scores are tied. Team 1 will be saved as the winner. Go back to keep playing, or save as-is.</span>
        </div>
      )}

      {saveError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)' }}>
          <Icon name="AlertCircle" size={16} color="#DC2626" />
          <span className="font-caption text-sm" style={{ color: '#DC2626' }}>{saveError}</span>
        </div>
      )}

      {shareStatus === 'copied' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200">
          <Icon name="CheckCircle" size={14} color="#059669" />
          <span className="font-caption text-xs text-emerald-700">Recap copied to clipboard!</span>
        </div>
      )}

      {/* Actions */}
      {saved ? (
        <div className="space-y-3">
          <Button variant="default" fullWidth onClick={onShare} iconName="Share2" iconPosition="left" loading={shareStatus === 'sharing'}>Share Recap</Button>
          <Button variant="outline" fullWidth onClick={onNewGame} iconName="Plus" iconPosition="left">New Game</Button>
          <Button variant="outline" fullWidth onClick={onGoToHistory} iconName="Clock" iconPosition="left">View History</Button>
        </div>
      ) : (
        <div className="space-y-3">
          <Button variant="default" fullWidth onClick={onSave} loading={isSaving} iconName="Save" iconPosition="left" disabled={!isAdmin}>
            Save Game
          </Button>
          <Button variant="outline" fullWidth onClick={onBackToPlay} iconName="ArrowLeft" iconPosition="left">
            Keep Playing
          </Button>
        </div>
      )}
    </div>
  );
};

const PlayerList = ({ label, color, players, scores, icon }) => (
  <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
    <div className="px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: `${color}0a`, borderBottom: '1px solid var(--color-border)' }}>
      <Icon name={icon} size={14} color={color} />
      <h4 className="font-heading font-semibold text-sm" style={{ color }}>{label}</h4>
    </div>
    <div className="divide-y divide-[var(--color-border)]">
      {players.map((p) => (
        <div key={p.id} className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
              <Image src={p.avatar_url || ''} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <span className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)' }}>{p.name}</span>
          </div>
          <span className="font-data text-sm font-bold" style={{ color }}>{scores[p.id] || 0} pts</span>
        </div>
      ))}
    </div>
  </div>
);

export default LiveGamePage;
